/**
 * Вложения и ссылки комнаты — источник для табов «Медиа», «Ссылки», «Голосовые»
 * на странице комнаты (образец Антона 2026-09-10: телеграмные табы).
 *
 * ОТКУДА БЕРЁМ. Синапс не отдаёт готового списка вложений комнаты: штатного
 * endpoint'а «дай все картинки» в спецификации нет. Единственный честный
 * путь — идти назад по таймлайну и собирать события с вложениями. Поэтому
 * здесь ПОСТРАНИЧНАЯ подгрузка: сколько пролистали, столько и показываем,
 * с кнопкой «загрузить ещё». Врать про «23 media» до полного прохода нельзя,
 * и число в шапке подписано как «найдено», а не как «всего».
 *
 * ЧТО СЧИТАЕМ ЧЕМ:
 *  - Медиа — m.image и m.video (сетка превью, у видео — длительность);
 *  - Голосовые — m.audio с признаком голосового (MSC3245 org.matrix.msc3245.voice)
 *    ИЛИ обычное m.audio; отдельный таб, потому что в образце он отдельный;
 *  - Файлы — m.file, показываем в «Медиа» после картинок: своего таба у них
 *    в образце нет, а прятать их некуда;
 *  - Ссылки — http(s) из тела текстовых сообщений. Превью-карточку с
 *    заголовком и картинкой (как в образце) строит СЕРВЕР через
 *    /_matrix/media/v3/preview_url — см. fetchLinkPreview.
 */
import {
  Direction,
  EventTimeline,
  type MatrixClient,
  type MatrixEvent,
  type Room,
} from "matrix-js-sdk";
import { currentLocale, tr } from "../i18n";

export type AttachmentKind = "image" | "video" | "file" | "voice";

export interface Attachment {
  eventId: string;
  kind: AttachmentKind;
  /** mxc:// адрес самого файла. */
  mxc: string;
  /** mxc:// превью, если сервер его сделал (для видео обычно есть). */
  thumbMxc?: string;
  name: string;
  sizeBytes?: number;
  /** Длительность в миллисекундах — у видео и голосовых. */
  durationMs?: number;
  sender: string;
  ts: number;
}

export interface RoomLink {
  eventId: string;
  url: string;
  sender: string;
  ts: number;
}

interface ScanResult {
  attachments: Attachment[];
  links: RoomLink[];
  /** Есть ли ещё непросмотренная история. */
  hasMore: boolean;
  /** Сколько событий реально просмотрено — для честной подписи. */
  scanned: number;
}

const URL_RE = /https?:\/\/[^\s<>"')]+/g;

function contentOf(ev: MatrixEvent): Record<string, unknown> {
  return ev.getContent() as Record<string, unknown>;
}

/** Голосовое ли это m.audio: маркер MSC3245 либо признак waveform. */
function isVoice(c: Record<string, unknown>): boolean {
  if ("org.matrix.msc3245.voice" in c) return true;
  const info = c["org.matrix.msc1767.audio"] as { waveform?: unknown } | undefined;
  return Boolean(info?.waveform);
}

function kindOf(ev: MatrixEvent): AttachmentKind | null {
  const c = contentOf(ev);
  switch (c.msgtype) {
    case "m.image":
      return "image";
    case "m.video":
      return "video";
    case "m.audio":
      return isVoice(c) ? "voice" : "file";
    case "m.file":
      return "file";
    default:
      return null;
  }
}

function toAttachment(ev: MatrixEvent, kind: AttachmentKind): Attachment | null {
  const c = contentOf(ev);
  // Зашифрованные вложения (content.file) в этом клиенте не расшифровываем:
  // crypto выключен намеренно. Молча показать «битую картинку» хуже, чем
  // не показать — такие события пропускаем.
  const mxc = typeof c.url === "string" ? c.url : null;
  if (!mxc || !mxc.startsWith("mxc://")) return null;
  const info = (c.info ?? {}) as {
    thumbnail_url?: string;
    size?: number;
    duration?: number;
  };
  return {
    eventId: ev.getId() ?? "",
    kind,
    mxc,
    thumbMxc:
      typeof info.thumbnail_url === "string" && info.thumbnail_url.startsWith("mxc://")
        ? info.thumbnail_url
        : undefined,
    name: typeof c.body === "string" && c.body.trim() ? c.body : tr("ui.bez_imeni"),
    sizeBytes: typeof info.size === "number" ? info.size : undefined,
    durationMs: typeof info.duration === "number" ? info.duration : undefined,
    sender: ev.getSender() ?? "",
    ts: ev.getTs(),
  };
}

/**
 * Пройти назад по истории и собрать вложения и ссылки.
 *
 * `targetEvents` — сколько событий пролистать за один заход. Не «сколько
 * вложений найти»: в комнате может не быть ни одного, и бесконечный цикл
 * поиска повесил бы вкладку.
 */
export async function scanRoomAttachments(
  client: MatrixClient,
  room: Room,
  targetEvents = 600,
): Promise<ScanResult> {
  const live = room.getLiveTimeline();
  let scanned = 0;
  let hasMore = true;

  // Листаем назад, пока не наберём бюджет событий или не кончится история.
  for (let guard = 0; guard < 40; guard += 1) {
    const total = room
      .getUnfilteredTimelineSet()
      .getTimelines()
      .reduce((n, t) => n + t.getEvents().length, 0);
    if (total >= targetEvents) break;
    const more = await client.paginateEventTimeline(
      room.getUnfilteredTimelineSet().getLiveTimeline() ?? live,
      { backwards: true, limit: 200 },
    );
    if (!more) {
      hasMore = false;
      break;
    }
  }

  const attachments: Attachment[] = [];
  const links: RoomLink[] = [];
  const seenUrls = new Set<string>();

  for (const timeline of room.getUnfilteredTimelineSet().getTimelines()) {
    for (const ev of timeline.getEvents()) {
      if (ev.getType() !== "m.room.message") continue;
      if (ev.isRedacted()) continue;
      scanned += 1;

      const kind = kindOf(ev);
      if (kind) {
        const a = toAttachment(ev, kind);
        if (a) attachments.push(a);
        continue;
      }

      const c = contentOf(ev);
      if (c.msgtype !== "m.text" && c.msgtype !== "m.notice") continue;
      const body = typeof c.body === "string" ? c.body : "";
      for (const m of body.match(URL_RE) ?? []) {
        const url = m.replace(/[.,;:!?)]+$/, "");
        if (seenUrls.has(url)) continue;
        seenUrls.add(url);
        links.push({
          eventId: ev.getId() ?? "",
          url,
          sender: ev.getSender() ?? "",
          ts: ev.getTs(),
        });
      }
    }
  }

  // Новые сверху — как в образце (последний месяц первым).
  attachments.sort((a, b) => b.ts - a.ts);
  links.sort((a, b) => b.ts - a.ts);

  if (
    live.getPaginationToken(EventTimeline.BACKWARDS) === null &&
    room.getUnfilteredTimelineSet().getTimelines().length <= 1
  ) {
    hasMore = false;
  }

  return { attachments, links, hasMore, scanned };
}

/** Заголовок группы «AUGUST 2026» — как в образце. */
export function monthLabel(ts: number): string {
  return new Date(ts)
    .toLocaleDateString(currentLocale(), { month: "long", year: "numeric" })
    .toUpperCase();
}

/** Группировка по месяцу с сохранением порядка (новые сверху). */
export function groupByMonth<T extends { ts: number }>(items: T[]): [string, T[]][] {
  const out: [string, T[]][] = [];
  for (const item of items) {
    const label = monthLabel(item.ts);
    const last = out[out.length - 1];
    if (last && last[0] === label) last[1].push(item);
    else out.push([label, [item]]);
  }
  return out;
}

export interface LinkPreview {
  title?: string;
  description?: string;
  imageMxc?: string;
}

/**
 * Серверное превью ссылки: заголовок, описание, картинка — ровно то, что
 * рисует карточкой образец. Делает это СЕРВЕР (`preview_url`), клиент сам
 * на чужие сайты не ходит: иначе мы раскрывали бы IP человека каждому
 * домену из переписки.
 *
 * Возвращает null молча: preview_url часто отключён администратором, и это
 * нормальное состояние, а не ошибка, о которой надо кричать в интерфейсе.
 */
export async function fetchLinkPreview(
  client: MatrixClient,
  url: string,
): Promise<LinkPreview | null> {
  try {
    const r = (await client.getUrlPreview(url, Date.now())) as Record<string, unknown>;
    const title = typeof r["og:title"] === "string" ? r["og:title"] : undefined;
    const description =
      typeof r["og:description"] === "string" ? r["og:description"] : undefined;
    const image = typeof r["og:image"] === "string" ? r["og:image"] : undefined;
    if (!title && !description && !image) return null;
    return {
      title,
      description,
      imageMxc: image?.startsWith("mxc://") ? image : undefined,
    };
  } catch {
    return null;
  }
}

export { Direction };
